//# sourceMappingURL=redraw.mjs.map
/// <reference types="./redraw.d.mts" />
import * as $promise from "../gleam_javascript/gleam/javascript/promise.mjs";
import * as $option from "../gleam_stdlib/gleam/option.mjs";
import { None, Some } from "../gleam_stdlib/gleam/option.mjs";
import * as $string from "../gleam_stdlib/gleam/string.mjs";
import {
  contextProvider as provider,
  getContext as get_context,
  createContext as create_context,
} from "./context.ffi.mjs";
import { convertProps as convert_children } from "./external.ffi.mjs";
import { Ok, toList, CustomType as $CustomType, makeError } from "./gleam.mjs";
import {
  wrapReactComponent as wrap_react_component,
  setDisplayName as set_display_name,
  wrapCall as wrap_call,
  strictMode as strict_mode,
  fragment,
  strictMode as profiler,
  suspense,
  captureOwnerStack as capture_owner_stack,
  jsx,
  wrapComponent as wrap_element,
  wrapComponent as wrap_component,
  memoize,
  wrapStandalone as wrap_standalone,
} from "./redraw.ffi.mjs";
import * as $unsafe from "./redraw/internal/unsafe.mjs";
import * as $ref from "./redraw/ref.mjs";
import {
  useCallback as use_callback,
  useDebugValue as use_debug_value,
  useDebugValue as use_debug_value_,
  useDeferredValue as use_deferred_value,
  useEffect as use_effect,
  useEffect as use_effect_,
  useId as use_id,
  useInsertionEffect as use_insertion_effect,
  useInsertionEffect as use_insertion_effect_,
  useLayoutEffect as use_layout_effect,
  useLayoutEffect as use_layout_effect_,
  useMemo as use_memo,
  useReducer as use_reducer,
  useReducer as use_reducer_,
  useState as use_state,
  useState as use_state_,
  useState as use_lazy_state,
  useState as use_lazy_state_,
  useTransition as use_transition,
  useTransition as use_async_transition,
  useOptimistic as use_optimistic,
  useOptimistic as use_optimistic_,
  useOptimistic as use_optimistic_action,
  useActionState as use_action_state,
  useSyncExternalStore as use_sync_external_store,
  use as use_promise,
  useRef as use_ref_,
  useImperativeHandle as use_imperative_handle_,
  use as use_context,
  createContext as create_context_,
  act,
  startTransition as start_transition,
  memo,
  memo as memo_,
} from "react";

export {
  act,
  capture_owner_stack,
  create_context,
  create_context_,
  fragment,
  get_context,
  jsx,
  memo,
  memo_,
  memoize,
  profiler,
  provider,
  start_transition,
  strict_mode,
  suspense,
  use_action_state,
  use_async_transition,
  use_callback,
  use_context,
  use_debug_value,
  use_debug_value_,
  use_deferred_value,
  use_effect,
  use_effect_,
  use_id,
  use_imperative_handle_,
  use_insertion_effect,
  use_insertion_effect_,
  use_layout_effect,
  use_layout_effect_,
  use_lazy_state,
  use_lazy_state_,
  use_memo,
  use_optimistic,
  use_optimistic_,
  use_optimistic_action,
  use_promise,
  use_reducer,
  use_reducer_,
  use_ref_,
  use_state,
  use_state_,
  use_sync_external_store,
  use_transition,
};

const FILEPATH = "src/redraw.gleam";

/**
 * Error returned from `create_context_`.
 * Context with the corresponding `name` already exists.
 */
export class ExistingContext extends $CustomType {
  constructor(name) {
    super();
    this.name = name;
  }
}
export const Error$ExistingContext = (name) => new ExistingContext(name);
export const Error$isExistingContext = (value) =>
  value instanceof ExistingContext;
export const Error$ExistingContext$name = (value) => value.name;
export const Error$ExistingContext$0 = (value) => value.name;

/**
 * Error returned from `get_context`.
 * Context with the corresponding `name` does not exists.
 */
export class UnknownContext extends $CustomType {
  constructor(name) {
    super();
    this.name = name;
  }
}
export const Error$UnknownContext = (name) => new UnknownContext(name);
export const Error$isUnknownContext = (value) =>
  value instanceof UnknownContext;
export const Error$UnknownContext$name = (value) => value.name;
export const Error$UnknownContext$0 = (value) => value.name;

/**
 * Error returned from `capture_owner_stack`.
 * `capture_owner_stack` can only be used in development, and you're not
 * in development anymore.
 */
export class DevelopmentOnly extends $CustomType {}
export const Error$DevelopmentOnly = () => new DevelopmentOnly();
export const Error$isDevelopmentOnly = (value) =>
  value instanceof DevelopmentOnly;

/**
 * Error returned from `capture_owner_stack`.
 * `capture_owner_stack` can sometimes return `null`, when the stack is
 * unavailable.
 */
export class OwnerStackUnavailable extends $CustomType {}
export const Error$OwnerStackUnavailable = () => new OwnerStackUnavailable();
export const Error$isOwnerStackUnavailable = (value) =>
  value instanceof OwnerStackUnavailable;

class ReactComponent extends $CustomType {
  constructor(render, memoize) {
    super();
    this.render = render;
    this.memoize = memoize;
  }
}

export class Suspense extends $CustomType {
  constructor(fallback) {
    super();
    this.fallback = fallback;
  }
}
export const Suspense$Suspense = (fallback) => new Suspense(fallback);
export const Suspense$isSuspense = (value) => value instanceof Suspense;
export const Suspense$Suspense$fallback = (value) => value.fallback;
export const Suspense$Suspense$0 = (value) => value.fallback;

/**
 * Create a Redraw component, with a `name`, and a `render` function. A
 * `render` function is simply a function accepting props, and returning
 * an `Element`.
 *
 * ```gleam
 * pub fn my_component() {
 *   // Create your other Redraw components before defining the others.
 *   use my_other_component <- redraw.compose(my_other_component())
 *   // Define your component.
 *   use props: MyComponentProps <- redraw.component_("MyComponent")
 *   // Define some hooks.
 *   let #(state, set_state) = redraw.use_state(0)
 *   // Return an `Element`. Keep in mind an empty element can be defined
 *   // with `component_` with `Nil` as props.
 *   html.div([], [
 *     html.text("In my component"),
 *     my_other_component(MyOtherComponentProps),
 *   ])
 * }
 * ```
 */
export function component_(name, render) {
  let _pipe = render;
  let _pipe$1 = set_display_name(_pipe, name);
  let _pipe$2 = wrap_react_component(_pipe$1);
  return ((_capture) => { return new ReactComponent(_capture, false); })(
    _pipe$2,
  );
}

/**
 * Allow to compose and use other components within a component. `compose` is
 * the only way to use components with other components. `compose` should be
 * called during the bootstrap phase of `redraw`, and let you access the
 * underlying component.
 *
 * ```gleam
 * fn component() {
 *   use other_component <- redraw.compose(other_component())
 *   h.div([], [
 *     other_component(),
 *     h.div([], []),
 *   ])
 * }
 * ```
 */
export function compose(component, return$) {
  let render = wrap_call(component);
  return return$(render);
}

/**
 * Accepts a Component, and wrapp it in `React.memo`. `React.memo` ensures
 * that a component will never repaint when props are identical between two
 * renders. Redraw applies an algorithm suited to Gleam, to avoid you the
 * hard task of comparing props, while providing a correct comparison
 * algorithm.
 *
 * > While such optimisation could be done for every component,
 * > being able to select if a component should be memoized or not can help
 * > finding and solving bugs.
 * > [Take a look at the React documentation to get a better idea if you
 * > need memoization or not](https://react.dev/reference/react/memo#should-you-add-memo-everywhere).
 *
 * ```gleam
 * pub fn my_component() {
 *   // Create your other Redraw components before defining the others.
 *   use my_other_component <- redraw.compose(my_other_component())
 *   // Define your component.
 *   redraw.component_("MyComponent", fn (props: MyComponentProps) {
 *     // Define some hooks.
 *     let #(state, set_state) = redraw.use_state(0)
 *     // Return an `Element`. Keep in mind an empty element can
 *     // be defined with `memoized` with `Nil` as props.
 *     html.div([], [
 *       html.text("In my component"),
 *       my_other_component(MyOtherComponentProps),
 *     ])
 *   })
 *   |> redraw.memoize_
 * }
 * ```
 */
export function memoize_(component) {
  return new ReactComponent(component.render, true);
}

/**
 * Let you reference a value that’s not needed for rendering.
 * Most used ref you'll want to create. They're automatically created to `None`,
 * and can be passed to `ref` prop or `use_imperative_handle`.
 * `Ref` are most of the time used to reference DOM node, in order to run
 * side-effects. In this case, you probably don't want the ref value to be
 * anything than `Option(a)`, unless you need to persist a value across renders
 * which is not part of the DOM. In that case, use `use_ref_`. \
 * [Documentation](https://react.dev/reference/react/useRef)
 */
export function use_ref() {
  return use_ref_(new None());
}

/**
 * Let you customize the handle exposed as a [ref](https://react.dev/learn/manipulating-the-dom-with-refs).
 * Use `use_imperative_handle` when you want to customize the data stored in
 * a ref. \
 * [Documentation](https://react.dev/reference/react/useImperativeHandle)
 */
export function use_imperative_handle(ref, handler, dependencies) {
  return ((_capture) => {
    return use_imperative_handle_(ref, _capture, dependencies);
  })(() => { return new Some(handler()); });
}

/**
 * Redraw does not support passing key element to components in an easy way like
 * React does. To simplify this, it uses the same API than [Lustre](lustre.build)
 * to put keys on children.
 * ```gleam
 * fn my_component(props, children) {
 *   redraw.keyed(my_other_component(props, _), {
 *     use item <- list.map(children)
 *     #("my-key", item)
 *   })
 * }
 * ```
 */
export function keyed(element, content) {
  let content$1 = $unsafe.coerce(content);
  return element(content$1);
}

/**
 * Create a Redraw component, with a `name`, and a `render` function.
 * `render` will accept props, and a list of children.
 */
export function component(name, render) {
  let _pipe = render;
  let _pipe$1 = set_display_name(_pipe, name);
  return wrap_component(_pipe$1);
}

export function element(name, render) {
  let _pipe = render;
  let _pipe$1 = set_display_name(_pipe, name);
  return wrap_element(_pipe$1);
}

/**
 * Create a Redraw standalone component, with a `name` and a `render` function.
 * Keep in mind this component does not accept children nor props.
 */
export function standalone(name, render) {
  let _pipe = render;
  let _pipe$1 = set_display_name(_pipe, name);
  return wrap_standalone(_pipe$1);
}

/**
 * Convert a React component to a React-redraw component with children. Give it a
 * name, and send directly the FFI. Don't worry about the snake_case over
 * camelCase, redraw take care of it for you.
 *
 * Because `children` can accept anything, it's up to you to define the type of
 * the children. If you send a `List`, it will be automatically converted to a
 * JavaScript array.
 *
 * ```gleam
 * import redraw
 *
 * pub type MyComponentProps {
 *   MyComponentProps(
 *     first_prop: Bool,
 *     second_prop: String,
 *   )
 * }
 *
 * @external(javascript, "my_library", "MyComponent")
 * fn do_my_component(props: MyComponentProps) -> redraw.Component
 *
 * pub fn my_component() -> fn(MyComponentProps, List(Element)) -> redraw.Component {
 *   redraw.to_component("MyComponent", do_my_component)
 * }
 * ```
 */
export function to_component(name, render) {
  return ((_capture) => { return set_display_name(_capture, name); })(
    (props, children) => {
      let props$1 = convert_children(props);
      return jsx(render, props$1, children, true);
    },
  );
}

/**
 * Convert a React Component to a Redraw Element. Give it a
 * name, and send directly the FFI. Don't worry about the snake_case over
 * camelCase, redraw take care of it for you.
 *
 * ```gleam
 * import redraw
 *
 * pub type MyComponentProps {
 *   MyComponentProps(
 *     first_prop: Bool,
 *     second_prop: String,
 *   )
 * }
 *
 * @external(javascript, "my_library", "MyComponent")
 * fn do_my_component(props: MyComponentProps) -> redraw.Component
 *
 * pub fn my_component() -> fn(MyComponentProps) -> redraw.Component {
 *   redraw.to_element("MyComponent", do_my_component)
 * }
 * ```
 */
export function to_element(name, render) {
  return ((_capture) => { return set_display_name(_capture, name); })(
    (props) => {
      let props$1 = convert_children(props);
      return jsx(render, props$1, undefined, false);
    },
  );
}

/**
 * Set the current value of a ref, overriding its existing content.
 */
export function set_current(ref, value) {
  return $ref.assign(ref, value);
}

/**
 * Get the current value of a ref.
 */
export function get_current(ref) {
  return $ref.current(ref);
}

/**
 * `context` emulates classic Context usage in React. Instead of calling
 * `create_context` and `get_context`, it's possible to simply call `context`,
 * which will get or create the context directly, and allows to write code as
 * if Context is globally available. `context` also tries to preserve
 * type-checking at most. `context.default_value` is lazily evaluated, meaning
 * no additional computations will ever be run.
 *
 * ```gleam
 * import redraw
 *
 * const context_name = "MyContextName"
 *
 * pub type MyContext {
 *   MyContext(count: Int, set_count: fn (Int) -> Nil)
 * }
 *
 * fn default_value() {
 *   let count = 0
 *   les set_count = fn (_) { Nil }
 *   MyContext(count:)
 * }
 *
 * pub fn provider() {
 *   use _, children <- redraw.component()
 *   let context = redraw.context(context_name, default_value)
 *   let #(count, set_count) = redraw.use_state(0)
 *   redraw.provider(context, MyContext(count:, set_count:), children)
 * }
 *
 * pub fn use_my_context() {
 *   let context = redraw.context(context_name, default_value)
 *   redraw.use_context(context)
 * }
 * ```
 *
 * `context` should never fail, but it can be wrong if you use an already used
 * name.
 */
export function context(name, default_value) {
  let $ = get_context(name);
  if ($ instanceof Ok) {
    let context$1 = $[0];
    return context$1;
  } else {
    let get = $[0];
    let $1 = create_context(name, default_value());
    if ($1 instanceof Ok) {
      let context$1 = $1[0];
      return context$1;
    } else {
      let create = $1[0];
      let head = "[Redraw Internal Error] Unable to find or create context.";
      let get$1 = "  get_context: " + $string.inspect(get);
      let create$1 = "  create_context: " + $string.inspect(create);
      let body = ((_capture) => { return $string.join(_capture, " "); })(
        toList([
          "context should never panic.",
          "Please, open an issue on https://github.com/ghivert/redraw,",
          "and join the error details.\n",
        ]),
      );
      let details = "Error details:";
      let msg = $string.join(
        toList([head, body, details, get$1, create$1]),
        "\n",
      );
      throw makeError("panic", FILEPATH, "redraw", 821, "context", msg, {})
    }
  }
}
